import React from 'react';
import { motion } from 'framer-motion';
import { OFFICIAL_LINKS } from '../data/servicesData';
import { ShieldCheck, Zap, Percent, Headphones, Sparkles, ExternalLink, ArrowRight, Send, MessageCircle } from 'lucide-react';

export const HeroSection = ({
  onOpenInquiry,
  onExploreClick,
}) => {
  return (
    <section className="bg-white border-b border-slate-200 pt-7 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Prominent High-Contrast Professional Platform Update Banner */}
        <div className="max-w-5xl mx-auto mb-8">
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#071c45] via-[#0c2e6b] to-[#041636] p-3.5 sm:p-4 shadow-xl shadow-blue-950/20 border-2 border-blue-400/50 text-white flex flex-col md:flex-row items-center justify-between gap-3.5"
          >
            {/* Glowing Accent Background Effect */}
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-emerald-500/20 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-blue-500/20 rounded-full blur-2xl pointer-events-none" />

            <div className="flex items-center gap-3 relative z-10 w-full md:w-auto">
              {/* Dynamic Live Pulse */}
              <div className="flex h-3.5 w-3.5 relative shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-400"></span>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-400/50 px-2.5 py-0.5 rounded-full shadow-xs">
                  Live Platform Update
                </span>
                <p className="text-xs sm:text-sm font-bold text-white tracking-tight leading-snug">
                  Instant T+0, T+1 payouts are live, multi-lender loan platform is live, and insurance portal is live
                </p>
              </div>
            </div>

            <div className="relative z-10 flex items-center gap-2 w-full md:w-auto justify-end shrink-0">
              <button
                onClick={() => onOpenInquiry('General Enquiry')}
                className="w-full md:w-auto px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-black uppercase tracking-wider transition-all duration-200 shadow-md shadow-emerald-500/30 cursor-pointer flex items-center justify-center gap-1.5"
              >
                <span>Get Started</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Hero Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <h1 className="text-3xl sm:text-5xl font-black text-[#0c2e6b] tracking-tight leading-tight">
            Pay-In, Payout &amp; Banking Solutions for Your Business
          </h1>

          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Authorized aggregator pipelines with PhonePe, Paytm, Razorpay, Cashfree, Unlimit, and Subpaisa. Instant merchant-to-customer payouts, smart POS, loans, and IRDA-backed insurance.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <button
              onClick={() => onOpenInquiry('Payment Gateway (Pay-In)')}
              className="px-6 py-3 rounded-2xl bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm shadow-md shadow-blue-700/20 transition-all cursor-pointer flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              <span>Submit Request</span>
            </button>
            <a
              href={OFFICIAL_LINKS.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all shadow-md shadow-emerald-600/20 cursor-pointer flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp Us (+91 {OFFICIAL_LINKS.phone})</span>
            </a>
            <button
              onClick={onExploreClick}
              className="px-6 py-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm transition-colors cursor-pointer"
            >
              Explore Solutions
            </button>
            <button
              onClick={() => onOpenInquiry('Become a Partner (Refer & Earn)')}
              className="px-5 py-3 rounded-2xl border border-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100 font-bold text-sm transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <Sparkles className="w-4 h-4 text-amber-600" />
              <span>Become a Partner</span>
            </button>
          </div>
        </div>

        {/* 4 Value Points */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 max-w-4xl mx-auto text-center">
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors">
            <ShieldCheck className="w-5 h-5 text-blue-700 mx-auto mb-1.5" />
            <h4 className="font-bold text-sm text-[#0c2e6b]">Bank-Grade Security</h4>
            <p className="text-xs text-slate-500 mt-0.5">256-bit encryption &amp; RBI compliance</p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors">
            <Zap className="w-5 h-5 text-blue-700 mx-auto mb-1.5" />
            <h4 className="font-bold text-sm text-[#0c2e6b]">Faster Settlements</h4>
            <p className="text-xs text-slate-500 mt-0.5">Instant T+0 &amp; T+1 direct payouts</p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors">
            <Percent className="w-5 h-5 text-blue-700 mx-auto mb-1.5" />
            <h4 className="font-bold text-sm text-[#0c2e6b]">Best Market Rates</h4>
            <p className="text-xs text-slate-500 mt-0.5">Lowest MDR &amp; high partner payouts</p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors">
            <Headphones className="w-5 h-5 text-blue-700 mx-auto mb-1.5" />
            <h4 className="font-bold text-sm text-[#0c2e6b]">Dedicated Support</h4>
            <p className="text-xs text-slate-500 mt-0.5">Direct phone &amp; WhatsApp desk</p>
          </div>
        </div>

        {/* Official Direct Gateway Onboarding Cards: Apply Razorpay & Apply Cashfree */}
        <div className="mt-8 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Apply Razorpay Card with Animated Button */}
          <div className="p-5 rounded-2xl bg-blue-50/70 border-2 border-blue-200/90 flex flex-col justify-between space-y-4 hover:border-blue-400 transition-all shadow-sm">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-black text-lg text-blue-950">Apply Razorpay</span>
                <span className="text-[11px] font-bold text-blue-800 bg-blue-100/90 border border-blue-300 px-2.5 py-0.5 rounded-full">
                  Official Onboarding Rail
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Direct merchant onboarding with preferential sub-merchant MDR slabs, automated KYC, and unified checkout support.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {/* Linked Submit Button with animation */}
              <motion.button
                whileHover={{ scale: 1.02, x: 2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onOpenInquiry('Apply Razorpay Onboarding')}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950 text-white font-extrabold text-xs uppercase tracking-wider text-center transition-all shadow-md shadow-blue-800/25 flex items-center justify-center gap-2 cursor-pointer group"
              >
                <span>Submit Request</span>
                <motion.span 
                  animate={{ x: [0, 4, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <ArrowRight className="w-4 h-4 text-blue-200 group-hover:text-white" />
                </motion.span>
              </motion.button>

              <a
                href={`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent('Hello Fi Pay Bills, I want to onboard and Apply for Razorpay payment gateway.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 px-3.5 rounded-xl border border-emerald-400 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                title="Chat on WhatsApp about Razorpay"
              >
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                <span>WhatsApp</span>
              </a>

              <a
                href={OFFICIAL_LINKS.razorpayReferral}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 px-3.5 rounded-xl border border-blue-300 bg-white hover:bg-blue-100/60 text-blue-900 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                title="Direct Portal Link"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Apply Cashfree Card with Animated Button */}
          <div className="p-5 rounded-2xl bg-emerald-50/70 border-2 border-emerald-200/90 flex flex-col justify-between space-y-4 hover:border-emerald-400 transition-all shadow-sm">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-black text-lg text-emerald-950">Apply Cashfree</span>
                <span className="text-[11px] font-bold text-emerald-800 bg-emerald-100/90 border border-emerald-300 px-2.5 py-0.5 rounded-full">
                  Official Onboarding Rail
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Fast pay-in and bulk merchant-to-customer payouts via official partner link with instant API credentials.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {/* Linked Submit Button with animation */}
              <motion.button
                whileHover={{ scale: 1.02, x: 2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onOpenInquiry('Apply Cashfree Onboarding')}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-700 to-teal-900 hover:from-emerald-800 hover:to-teal-950 text-white font-extrabold text-xs uppercase tracking-wider text-center transition-all shadow-md shadow-emerald-800/25 flex items-center justify-center gap-2 cursor-pointer group"
              >
                <span>Submit Request</span>
                <motion.span 
                  animate={{ x: [0, 4, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <ArrowRight className="w-4 h-4 text-emerald-200 group-hover:text-white" />
                </motion.span>
              </motion.button>

              <a
                href={`https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent('Hello Fi Pay Bills, I want to onboard and Apply for Cashfree payment gateway & payouts.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 px-3.5 rounded-xl border border-emerald-400 bg-white hover:bg-emerald-100/60 text-emerald-800 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                title="Chat on WhatsApp about Cashfree"
              >
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                <span>WhatsApp</span>
              </a>

              <a
                href={OFFICIAL_LINKS.cashfreeReferral}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 px-3.5 rounded-xl border border-emerald-300 bg-white hover:bg-emerald-100/60 text-emerald-900 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                title="Direct Portal Link"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Supported Aggregators Strip with Subpaisa (strictly no space) */}
        <div className="mt-8 pt-6 border-t border-slate-100 text-center">
          <p className="text-xs text-slate-500 font-semibold mb-3">
            Payment Gateway &amp; POS Aggregators Supported
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs font-bold text-slate-700">
            <span className="px-3 py-1 bg-slate-100 rounded-lg">PhonePe</span>
            <span className="px-3 py-1 bg-slate-100 rounded-lg">Paytm</span>
            <span className="px-3 py-1 bg-slate-100 rounded-lg">Razorpay</span>
            <span className="px-3 py-1 bg-slate-100 rounded-lg">Cashfree</span>
            <span className="px-3 py-1 bg-slate-100 rounded-lg">Pine Labs</span>
            <span className="px-3 py-1 bg-slate-100 rounded-lg">BharatPe</span>
            <span className="px-3 py-1 bg-slate-100 rounded-lg">Unlimit</span>
            <span className="px-3 py-1 bg-blue-100 text-blue-900 font-black rounded-lg border border-blue-200 shadow-2xs">
              Subpaisa
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
